import * as THREE from "three";
import type { PhysicsHandle } from "./physics";

export interface DebugStats {
  setVisible: (v: boolean) => void;
  /** Called once per frame from the scene update loop. */
  update: (dt: number) => void;
}

interface PerfMemory {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
}

/**
 * Lightweight stats overlay: FPS, frame time, three.js renderer info,
 * Rapier body count, and (Chrome only) JS heap. Updates throttled to
 * 4 Hz so the DOM doesn't churn — readings are still moving averages
 * over the last ~250 ms.
 */
export function createDebugStats(
  renderer: THREE.WebGLRenderer,
  physics: PhysicsHandle,
): DebugStats {
  const panel = document.getElementById("debug-panel") as HTMLElement;
  const fpsEl = document.getElementById("debug-fps") as HTMLElement;
  const ftEl = document.getElementById("debug-frametime") as HTMLElement;
  const callsEl = document.getElementById("debug-calls") as HTMLElement;
  const trisEl = document.getElementById("debug-triangles") as HTMLElement;
  const geoEl = document.getElementById("debug-geometries") as HTMLElement;
  const texEl = document.getElementById("debug-textures") as HTMLElement;
  const bodiesEl = document.getElementById("debug-bodies") as HTMLElement;
  const heapEl = document.getElementById("debug-heap") as HTMLElement;

  let visible = false;
  let frames = 0;
  let elapsed = 0;
  const REFRESH_INTERVAL = 0.25;

  function fmtThousands(n: number): string {
    return n.toLocaleString("en-US");
  }

  function fmtHeap(): string {
    const mem = (performance as unknown as { memory?: PerfMemory }).memory;
    if (!mem) return "n/a";
    const mb = mem.usedJSHeapSize / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  }

  return {
    setVisible(v) {
      visible = v;
      panel.dataset.open = String(v);
      panel.setAttribute("aria-hidden", String(!v));
    },
    update(dt) {
      if (!visible) return;
      frames++;
      elapsed += dt;
      if (elapsed < REFRESH_INTERVAL) return;

      const fps = frames / elapsed;
      const ms = (elapsed / frames) * 1000;
      const r = renderer.info.render;
      const m = renderer.info.memory;

      fpsEl.textContent = fps.toFixed(0);
      ftEl.textContent = `${ms.toFixed(1)} ms`;
      callsEl.textContent = fmtThousands(r.calls);
      trisEl.textContent = fmtThousands(r.triangles);
      geoEl.textContent = fmtThousands(m.geometries);
      texEl.textContent = fmtThousands(m.textures);
      bodiesEl.textContent = fmtThousands(physics.world.bodies.len());
      heapEl.textContent = fmtHeap();

      frames = 0;
      elapsed = 0;
    },
  };
}
